import { LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserRole } from "@/services/types/common.types";
import { useAuth, useAuthStore } from "@/store";
import { SidebarItem } from "./SidebarItem";
import { navItems } from "./sidebarNavItems";

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  currentPage: string;
}

export const Sidebar = React.memo(function Sidebar({
  isOpen,
  currentPage,
}: SidebarProps) {
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const [activeTab, setActiveTab] = useState<string>(currentPage);

  const { logout } = useAuth();
  const userRoles = useAuthStore((state) => state.user?.roles ?? []);
  const isTenantAdmin = userRoles.includes(UserRole.ADMIN);
  const isCreator = userRoles.includes(UserRole.CREATOR);

  const visibleNavItems = useMemo(() => {
    return navItems.filter((item) => {
      if (item.hidden) return false;
      if (item.deniedRoles?.some((role) => userRoles.includes(role))) {
        return false;
      }

      const allowedRoles = item.allowedRoles as UserRole[] | undefined;

      if (!allowedRoles?.length) {
        return true;
      }

      return allowedRoles.some((role) => userRoles.includes(role));
    });
  }, [userRoles]);

  const commonNavItems = useMemo(() => {
    const tenantOnlyKeys = new Set([
      "teachers",
      "students",
      "academic-setup",
      "time-table",
      "announcements",
      "content-creators",
      "content-studio",
    ]);
    const creatorOnlyKeys = new Set(["test-prep"]);
    return visibleNavItems.filter(
      (item) => !tenantOnlyKeys.has(item.key) && !creatorOnlyKeys.has(item.key),
    );
  }, [visibleNavItems]);

  const tenantAdminNavItems = useMemo(() => {
    const tenantOnlyKeys = new Set([
      "teachers",
      "students",
      "academic-setup",
      "time-table",
      "announcements",
      "content-creators",
    ]);
    return visibleNavItems.filter((item) => tenantOnlyKeys.has(item.key));
  }, [visibleNavItems]);

  const creatorNavItems = useMemo(() => {
    const creatorKeys = new Set(["test-prep","content-studio"]);
    return visibleNavItems.filter((item) => creatorKeys.has(item.key));
  }, [visibleNavItems]);

  useEffect(() => {
    setActiveTab(currentPage);
  }, [currentPage]);

  const handleSidebarBtnClick = useCallback(
    (key: string) => {
      navigate(`/${key}`);
    },
    [navigate],
  );

  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } catch (error) {
      console.log("ERR", error);
    }
  }, [logout]);
  return (
    <aside
      className={cn(
        "flex flex-col bg-[var(--surface-subtle-hex)] h-full transition-all duration-300",
        isOpen ? "w-44 " : "w-20 ",
        "hidden md:flex ",
      )}
    >
      <div className="flex-shrink-0 p-4">
        <div className="flex items-center justify-center gap-2">
          <img
            src="/assets/icons/vidyapeeth-logo.svg"
            alt="Vidyapeeth Logo"
            className={cn("object-contain transition-all duration-300", isOpen ? "h-10" : "h-8")}
          />
          {isOpen && (
            <span
              style={{
                fontFamily: "Inter, sans-serif",
                fontStyle: "normal",
                fontWeight: 700,
                color: "rgb(69, 69, 69)",
                fontSize: "16px",
                lineHeight: "24px",
              }}
            >
              VIDYAPEETH
            </span>
          )}
        </div>
      </div>

      <nav className="flex-1 py-4 min-h-0 overflow-y-auto chat-scroll">
        {commonNavItems.map((item) => (
          <SidebarItem
            key={item.key}
            item={item}
            isActive={activeTab === item.key}
            isOpen={isOpen}
            onClick={() => !item.disabled && handleSidebarBtnClick(item.key)}
          />
        ))}

        {isTenantAdmin && tenantAdminNavItems.length > 0 && (
          <div className={cn("mt-3", isOpen ? "px-4" : "px-2")}>
            {tenantAdminNavItems.map((item) => (
              <SidebarItem
                key={item.key}
                item={item}
                isActive={activeTab === item.key}
                isOpen={isOpen}
                onClick={() =>
                  !item.disabled && handleSidebarBtnClick(item.key)
                }
              />
            ))}
          </div>
        )}

        {isCreator && creatorNavItems.length > 0 && (
          <div className={cn("mt-3", isOpen ? "px-4" : "px-2")}>
            {creatorNavItems.map((item) => (
              <SidebarItem
                key={item.key}
                item={item}
                isActive={activeTab === item.key}
                isOpen={isOpen}
                onClick={() =>
                  !item.disabled && handleSidebarBtnClick(item.key)
                }
              />
            ))}
          </div>
        )}
      </nav>

      <div className="flex-shrink-0 border-gray-200 border-t">
        <button
          className="flex flex-col justify-center items-center gap-[0.25rem] hover:bg-red-50 py-[0.75rem] w-full text-red-600"
          onClick={handleLogout}
        >
          <LogOut className="flex-shrink-0 w-5 h-5" />
          {isOpen && <span className="text-material-title-small">{t("sidebar.logout")}</span>}
        </button>
      </div>
    </aside>
  );
});
